import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import "@/i18n";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Geoportal from "@/pages/Geoportal";
import EsgDashboard from "@/pages/EsgDashboard";
import Pegada from "@/pages/Pegada";
import Pesaxes from "@/pages/Pesaxes";
import Settings from "@/pages/Settings";
import Incidencias from "@/pages/Incidencias";
import Inventario from "@/pages/Inventario";
import Informe from "@/pages/Informe";
import Auditoria from "@/pages/Auditoria";
import Admin from "@/pages/Admin";
import Documentacion from "@/pages/Documentacion";
import DatosAbertos from "@/pages/DatosAbertos";
import Estatisticas from "@/pages/Estatisticas";
import Monitoring from "@/pages/Monitoring";
import Impacto from "@/pages/Impacto";
import Frota from "@/pages/Frota";
import Contenedores from "@/pages/Contenedores";
import ProtectedRoute from "@/components/layout/ProtectedRoute";
import RequireRole from "@/components/layout/RequireRole";
import AppShell from "@/components/layout/AppShell";
import {
  Transparencia,
  Privacidade,
  Accesibilidade,
} from "@/pages/legal";

function DocumentHead() {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    const brand = t("brand.name");
    document.title = `${brand} · Plataforma DTI/PID · Mancomunidade do Salnés`;
    const set = (selector, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute("content", value);
    };
    set('meta[name="description"]', `${brand} — Plataforma DTI/PID da Mancomunidade do Salnés. Financiado pola UE — NextGenerationEU.`);
    set('meta[property="og:title"]', `${brand} · Plataforma DTI/PID`);
    set('meta[property="og:site_name"]', brand);
    set('meta[name="twitter:title"]', `${brand} · Plataforma DTI/PID`);
    // update <html lang>
    document.documentElement.lang = i18n.language?.startsWith("es") ? "es" : "gl";
  }, [t, i18n.language]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <DocumentHead />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/transparencia" element={<Transparencia />} />
          <Route path="/politica-privacidade" element={<Privacidade />} />
          <Route path="/accesibilidade" element={<Accesibilidade />} />
          <Route path="/datos-abertos" element={<DatosAbertos />} />
          <Route path="/documentacion" element={<Documentacion />} />
          <Route path="/estatisticas" element={<Estatisticas />} />
          <Route path="/impacto" element={<Impacto />} />

          {/* Deep-link para documentación externa: /admin/api-exposicion → panel Admin embebido */}
          <Route path="/admin/api-exposicion" element={<Navigate to="/app/admin/api-exposicion" replace />} />

          <Route element={<ProtectedRoute />}>
            <Route path="/app" element={<AppShell />}>
              <Route index element={<Dashboard />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="geoportal" element={<Geoportal />} />
              <Route path="frota" element={<Frota />} />
              <Route path="contedores" element={<Contenedores />} />
              <Route path="esg" element={<EsgDashboard />} />
              <Route path="pegada" element={<Pegada />} />
              <Route path="incidencias" element={<Incidencias />} />
              <Route path="inventario" element={<Inventario />} />
              <Route path="informe" element={<Informe />} />
              <Route path="pesaxes" element={
                <RequireRole role={["ADMIN","SUPERVISOR"]}><Pesaxes /></RequireRole>
              } />
              <Route path="auditoria" element={
                <RequireRole role="ADMIN"><Auditoria /></RequireRole>
              } />
              <Route path="admin" element={
                <RequireRole role="ADMIN"><Admin /></RequireRole>
              } />
              <Route path="admin/:tab" element={
                <RequireRole role="ADMIN"><Admin /></RequireRole>
              } />
              <Route path="monitoring" element={
                <RequireRole role="ADMIN"><Monitoring /></RequireRole>
              } />
              <Route path="settings" element={<Settings />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </AuthProvider>
  );
}

export default App;
